import { formatDate } from '@/lib/date-time';
/**
 * @file routes/_app/audit/orgs/$clientId.tsx
 * @description 組織詳情頁面（M1）。
 *
 * 顯示單一客戶組織的完整資料：
 * - 基本資料（名稱、驗證註冊碼、方案、代表人、聯絡方式）
 * - 歷史驗證案件（案件列表，可點擊查看案件詳情）
 *
 * 視覺：PageShell + SectionCard + StatusPill + DataField
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Alert, Button, Card, Col, Descriptions, Row, Table, Tag, Tabs, Typography } from 'antd';
import { ArrowLeft, Building2, Mail, Phone } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { apiClient } from '@/lib/api-client';
import { PageShell, StatusPill, type StatusPillProps } from '@/components/shell';

/** TanStack Router 路由定義 */
export const Route = createFileRoute('/_app/audit/orgs/$clientId')({ component: OrgDetailPage });

// ─── 類型定義 ─────────────────────────────────────────────────────────────────

interface Client {
  id: number;
  clientCode: string;
  name: string;
  // 驗證業務屬性（方案/資格/類型/類別/選項/合約來源）已下放至案件，客戶不再承載
  taxId: string | null;
  // 法人（含身份證號）
  representativeName: string | null;
  representativeIdNo: string | null;
  representativeMobile: string | null;
  representativeEmail: string | null;
  // 負責人（無身份證號）
  personInChargeName: string | null;
  personInChargePhone: string | null;
  personInChargeEmail: string | null;
  registeredAddress: string | null;
  mailingAddress: string | null;
  verificationStatus: string;
  paymentStatus: string;
  contractExpiresAt: string | null;
  createdAt: string;
}

interface CaseItem {
  id: number;
  caseCode: string;
  verificationType: string;
  workflowState: string;
  dueDate: string | null;
  createdAt: string;
}

// ─── 狀態顏色映射 ─────────────────────────────────────────────────────────────

const STATE_TONE: Record<string, StatusPillProps['tone']> = {
  受理建檔: 'info',
  已派案: 'brand',
  已繳費: 'success',
  稽核中: 'warning',
  報告中: 'brand',
  結案: 'muted',
  NC整改中: 'danger',
};

const VS_TONE: Record<string, StatusPillProps['tone']> = {
  新申請: 'info',
  維持: 'success',
  結束驗證: 'muted',
  撤銷驗證: 'danger',
};

// ─── 組件 ────────────────────────────────────────────────────────────────────

/**
 * 組織詳情頁面主組件。
 */
function OrgDetailPage() {
  const navigate = useNavigate();
  const { clientId } = Route.useParams();

  const { data, isLoading, isError } = useQuery<{ item: Client }>({
    queryKey: ['client-detail', clientId],
    queryFn: () => apiClient.get(`/api/settings/clients/${clientId}`),
  });

  const { data: casesData } = useQuery<{ items: CaseItem[] }>({
    queryKey: ['client-cases', clientId],
    queryFn: () => apiClient.get(`/api/cases?clientId=${clientId}&pageSize=50`),
  });

  const client = data?.item;
  const cases = casesData?.items ?? [];

  if (isLoading)
    return (
      <div className="p-6">
        <Typography.Text>載入中…</Typography.Text>
      </div>
    );
  if (isError || !client)
    return (
      <div className="p-6">
        <Alert type="error" message="組織不存在" />
      </div>
    );

  const caseColumns: ColumnsType<CaseItem> = [
    {
      title: '案件編號',
      dataIndex: 'caseCode',
      width: 160,
      render: (code: string, record) => (
        <Button
          type="link"
          size="small"
          style={{ fontFamily: 'monospace', padding: 0 }}
          onClick={() =>
            void navigate({ to: '/dispatch/$caseId', params: { caseId: String(record.id) } })
          }
        >
          {code}
        </Button>
      ),
    },
    { title: '驗證類型', dataIndex: 'verificationType', width: 120 },
    {
      title: '狀態',
      dataIndex: 'workflowState',
      width: 120,
      render: (s: string) => (
        <StatusPill tone={STATE_TONE[s] ?? 'neutral'} dot size="sm">
          {s}
        </StatusPill>
      ),
    },
    {
      title: '到期日',
      dataIndex: 'dueDate',
      width: 120,
      render: (d: string | null) => (d ? formatDate(d) : '—'),
    },
    {
      title: '建立時間',
      dataIndex: 'createdAt',
      width: 120,
      render: (d: string) => formatDate(d),
    },
  ];

  const contractExpiresAt = client.contractExpiresAt ? new Date(client.contractExpiresAt) : null;
  const isExpiring = contractExpiresAt && contractExpiresAt.getTime() - Date.now() < 90 * 86400000;

  return (
    <PageShell
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Building2 size={20} />
          {client.name}
        </span>
      }
      description={client.taxId ? `統一編號 ${client.taxId}` : '客戶主體資料'}
      meta={
        <>
          <Tag color="blue" style={{ fontFamily: 'monospace' }}>
            {client.clientCode}
          </Tag>
          <StatusPill tone={VS_TONE[client.verificationStatus] ?? 'neutral'} dot>
            {client.verificationStatus}
          </StatusPill>
          <StatusPill tone={client.paymentStatus === '正常' ? 'success' : 'danger'} size="sm">
            費用{client.paymentStatus}
          </StatusPill>
        </>
      }
      actions={
        <Button onClick={() => void navigate({ to: '/audit/orgs' })} icon={<ArrowLeft size={14} />}>
          返回列表
        </Button>
      }
    >
      {/* 基本資料 */}
      <Card className="mb-4" size="small" title="基本資料">
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Descriptions size="small" column={1}>
              {/* 驗證方案/類型/類別等業務屬性已下放至案件，見下方「歷史案件」 */}
              <Descriptions.Item label="統一編號">{client.taxId ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="法人">{client.representativeName ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="負責人">
                {client.personInChargeName ?? '—'}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item
                label={
                  <span>
                    <Phone size={12} style={{ marginRight: 4 }} />
                    法人電話
                  </span>
                }
              >
                {client.representativeMobile ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <span>
                    <Mail size={12} style={{ marginRight: 4 }} />
                    法人 Email
                  </span>
                }
              >
                {client.representativeEmail ?? '—'}
              </Descriptions.Item>
              <Descriptions.Item label="合約到期">
                {contractExpiresAt ? (
                  <span style={{ color: isExpiring ? '#e11d48' : undefined }}>
                    {formatDate(contractExpiresAt)}
                    {isExpiring && ' ⚠️ 即將到期'}
                  </span>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="建檔時間">
                {formatDate(client.createdAt)}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* 詳情 Tabs */}
      <Card>
        <Tabs
          items={[
            {
              key: 'cases',
              label: `驗證案件 (${cases.length})`,
              children: (
                <Table<CaseItem>
                  dataSource={cases}
                  rowKey="id"
                  size="small"
                  columns={caseColumns}
                  pagination={false}
                />
              ),
            },
            {
              key: 'contacts',
              label: '聯絡人',
              children: (
                <Alert
                  type="info"
                  message="聯絡人管理功能"
                  description="聯絡人列表與新增將在後續版本完善（可透過稽核專區 → 客戶管理 管理）。"
                />
              ),
            },
            {
              key: 'lands',
              label: '地塊',
              children: (
                <Alert
                  type="info"
                  message="地塊管理功能"
                  description="地塊列表與新增將在後續版本完善。"
                />
              ),
            },
            {
              key: 'sites',
              label: '加工場',
              children: (
                <Alert
                  type="info"
                  message="加工場管理功能"
                  description="加工場列表與新增將在後續版本完善。"
                />
              ),
            },
          ]}
        />
      </Card>
    </PageShell>
  );
}
